<template>
    <b-form>
        <b-alert
            show
            variant="warning"
            v-if="data.isDefaultProvider && data.bundle_id.length > 0"
        >
            <span id="dialog-alert-message">
                {{ CLINIC_PROVIDER_ALERT.LAB_RADIOLOGY_BUNDLE }}
            </span>
        </b-alert>
        <b-form-group :label-for="`bundle_id`" :label-cols="3" label="Bundle:">
            <v-select
                id="bundle_id"
                name="bundle_id"
                ref="vselect"
                :options="bundle_options_exclude"
                v-model="data.bundle_id"
                v-validate="'required'"
                placeholder="Select bundle (required)"
                multiple
                label="name"
                :class="{ 'border-danger': errors.has('bundle_id') }"
            />
        </b-form-group>
        <b-form-group
            v-if="data.client_id"
            label="Patient:"
            label-cols="3"
            label-for="patient_id"
        >
            <v-select
                id="patient_id"
                ref="vselect"
                v-model="data.patient"
                v-validate="
                    `${
                        this.data.record_option == CREATERECORD
                            ? 'required'
                            : ''
                    }`
                "
                :options="patients"
                :class="{
                    'border-danger': errors.has('patient_id'),
                    rounded: true
                }"
                :placeholder="patientPlaceholder"
                name="patient_id"
                label="name"
            />
        </b-form-group>
        <b-form-group
            label="Provider:"
            class
            label-cols="3"
            label-for="staff_id"
        >
            <select
                id="staff_id"
                v-model="data.staff_id"
                class="form-control"
                v-validate="`${isInvoice ? 'required' : ''}`"
                :class="{ 'border-danger': errors.has('staff_id') }"
                :placeholder="providerPlaceholder"
                name="staff_id"
            >
                <option v-if="isInvoice" :value="null" disabled selected
                    >Provider (required)</option
                >
                <option v-else :value="null" selected
                    >Provider (optional)</option
                >
                <option
                    v-for="provider in providers"
                    :key="provider.id"
                    :value="provider.id"
                    >{{ provider.providerName }}</option
                >
            </select>
        </b-form-group>
        <b-form-group
            class="medical-note"
            :label-for="`soap`"
            :label-cols="3"
            label="Medical Note:"
        >
            <b-form-select
                id="soap"
                v-model="data.soap"
                :options="medicalNotes"
                name="soap"
            ></b-form-select>
        </b-form-group>
        <b-form-group
            v-if="isInvoice"
            label="Create Records:"
            class
            label-cols="3"
            label-for="record-option"
        >
            <select
                id="record-option"
                v-model="data.record_option"
                class="form-control"
                name="record-option"
            >
                <option value="1">Don't create records</option>
                <option value="0">Create records</option>
            </select>
        </b-form-group>
        <b-form-group :label-for="`start`" :label-cols="3" :label="'Date:'">
            <date-picker
                id="startDate"
                name="startDate"
                :config="datePickerOptions"
                v-validate="'required'"
                @dp-show="showDatePicker"
                v-model="data.startDate"
                placeholder="Date (required)"
                :class="{ 'border-danger': errors.has('startDate') }"
            />
        </b-form-group>
    </b-form>
</template>
<style></style>
<script>
import {
    get,
    forEach,
    union,
    find,
    map,
    difference,
    sortBy,
    filter
} from "lodash";
import vSelect from "vue-select";
import ClientServices from "@/services/client";
import TemplatesServices from "@/services/templates";
import StaffServices from "@/services/staff";
import PatientHistoryServices from "@/services/patient-history";
import CLINICPROVIDER from "@/constants/clinic-provider";
import TEMPLATES from "@/constants/templates";

const CREATERECORD = 0;
export default {
    components: { vSelect },
    props: {
        data: {
            type: Object,
            default: () => {}
        },
        parentLoading: {
            type: Boolean,
            default: false
        },
        isInvoice: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            medicalNotes: [{ value: -1, text: "No Medical Note" }],
            loading: false,
            bundles: [],
            providers: [],
            formError: [],
            patients: [],
            CREATERECORD: CREATERECORD,
            CLINIC_PROVIDER_ALERT: CLINICPROVIDER.CLINIC_PROVIDER_ALERT
        };
    },
    computed: {
        bundle_options_exclude() {
            return difference(this.bundles, this.data.bundle_id);
        },
        datePickerOptions() {
            return {
                format: this.getDateFormat(),
                widgetParent: "#addBundleModal"
            };
        },
        patientPlaceholder() {
            return this.data.record_option == CREATERECORD
                ? "Patient Name (required)"
                : "Patient Name (optional)";
        },
        providerPlaceholder() {
            return this.isInvoice
                ? "Provider (required)"
                : "Provider (optional)";
        }
    },
    watch: {
        "data.staff_id"(value) {
            if (value !== "" || !this.isInvoice) {
                if (this.providerCount === 0) {
                    this.data.staff_id = 0;
                } else {
                    if (value) this.setStaffId(this.providers);
                }
            }
        },
        "data.patient"(value) {
            this.medicalNotes = [{ value: -1, text: "No Medical Note" }];
            if (value) this.getMedicalNote(value.id);
        }
    },
    mounted() {
        this.loading = true;
        this.$nextTick(() => {
            this.$emit("beforeload");

            let process = [
                this.getBundle(),
                this.getProviders(),
                this.getPatient(),
                this.setDefaultDate()
            ];

            this.$emit("loading");
            Promise.all(process).then(() => {
                this.$emit("loaded");
            });

            this.$validator.reset();
            this.loading = false;
        });
    },
    methods: {
        showDatePicker(event) {
            // fix position of the date picker
            this._datePickerShow(event);
        },
        setDefaultDate() {
            this.data.startDate = this._now().toFormat(this._dateFormat);
        },
        getBundle() {
            let self = this;
            let params = this.isInvoice
                ? null
                : {
                      type: [
                          TEMPLATES.TYPE.BUNDLE,
                          TEMPLATES.TYPE.BUNDLE_ESTIMATE
                      ]
                  };
            return TemplatesServices.getBundles(params).then(function (
                response
            ) {
                if (response.message.code == 200) {
                    self.bundles = response.resources.map(bundle => {
                        const typeString =
                            parseInt(bundle.type) == TEMPLATES.TYPE.BUNDLE
                                ? "(Invoice)"
                                : parseInt(bundle.type) ==
                                  TEMPLATES.TYPE.BUNDLE_ESTIMATE
                                ? "(Estimate)"
                                : "";
                        bundle.name = `${bundle.name} ${typeString}`;
                        return bundle;
                    });
                }
            });
        },
        getProviders() {
            let self = this;
            self.data.isDefaultProvider = false;
            return StaffServices.getProviders().then(function (response) {
                self.providers = get(response, "resource.data", []);

                self.providers = map(self.providers, element => ({
                    ...element,
                    id: element.actor.id,
                    providerName:
                        element.actor.firstname + " " + element.actor.lastname
                }));

                // --- sort by provider name
                self.providers = sortBy(self.providers, ["providerName"]);

                if (self.providers.length === 0) {
                    self.data.staff_id = null;
                    self.providerCount = 0;
                } else {
                    self.setStaffId(self.providers);
                }
            });
        },
        setStaffId(providerOptions) {
            let self = this;
            let existingStaff = find(providerOptions, [
                "id",
                self.data.staff_id
            ]);
            let defaultClinicProvider = find(self.providers, [
                "is_default_provider",
                true
            ]);

            if (!existingStaff) {
                let currentStaff = find(providerOptions, [
                    "id",
                    this.$root.currentActor.id
                ]);

                // default the provider if cannot find provider
                if (defaultClinicProvider && !currentStaff) {
                    self.data.isDefaultProvider = true;
                    self.data.staff_id = defaultClinicProvider.id;
                } else {
                    if (this.isInvoice) {
                        self.data.staff_id = currentStaff
                            ? currentStaff.id
                            : providerOptions[0].id;
                    }
                }
            } else {
                if (defaultClinicProvider) {
                    self.data.isDefaultProvider =
                        self.data.staff_id == defaultClinicProvider.id;
                }
            }
        },
        async getPatient() {
            if (!this.data.client_id) return; // estimate can have null client
            let response = await ClientServices.getPatientsByClient(
                this.data.client_id,
                [1, 3], // 1:active, 3:deceased (ci show the deceased patient)
                false
            );

            this.patients = filter(response, res => {
                return res.isactive;
            });

            if (this.data.defaultPatientId) {
                // pre-select patient if value exist
                this.data.patient = find(this.patients, [
                    "id",
                    this.data.defaultPatientId
                ]);
            }
        },
        getMedicalNote(patientID) {
            let self = this;
            let medicalNote = [];
            if (patientID) {
                return PatientHistoryServices.getMedicalNoteList(
                    patientID
                ).then(function (response) {
                    if (response.resources) {
                        forEach(response.resources, function (value) {
                            let name = "";
                            switch (value.type) {
                                case 0:
                                case 1:
                                case 2:
                                    name =
                                        "Medical Note" +
                                        " (" +
                                        self
                                            ._toDateTime(value.date)
                                            .toFormat(self._dateFormat) +
                                        ")";
                                    break;
                                default:
                                    if (value.template_id) {
                                        name =
                                            value.template_name +
                                            " (" +
                                            self
                                                ._toDateTime(value.date)
                                                .toFormat(self._dateFormat) +
                                            ")";
                                    }
                            }
                            if (value.title) {
                                name =
                                    value.title +
                                    " (" +
                                    self
                                        ._toDateTime(value.date)
                                        .toFormat(self._dateFormat) +
                                    ")";
                            }

                            medicalNote.push({
                                value: value.id,
                                text: name,
                                date: value.date
                            });
                        });

                        self.medicalNotes = union(
                            self.medicalNotes,
                            medicalNote
                        );
                    }
                });
            }
        }
    }
};
</script>
